import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { useMutationWithError } from "../../../utils/hooks/useMutationWithError";
import {
  ConfirmDraftEventSignUpInput,
  useConfirmDraftEventSignUpMutation,
} from "./__generated__/useConfirmDraftEventSignUpMutation.graphql";

export const useConfirmDraftEventSignUp = () => {
  const { showToast } = useToast();
  const [confirmDraftEventSignUp, isConfirming] =
    useMutationWithError<useConfirmDraftEventSignUpMutation>(
      useConfirmDraftEventSignUpMutationString
    );

  const handleConfirmDraftEventSignUp = (
    input: ConfirmDraftEventSignUpInput,
    successCallback?: () => void,
    errorCallback?: (error: Error) => void
  ) => {
    confirmDraftEventSignUp({
      variables: { input },
      onCompleted: () => {
        successCallback?.();
      },
      onCompletedErrorCustom: () => {
        errorCallback?.(new Error(t`Unable to complete event signup.`));
        showToast({
          description: t`We couldn't complete your signup. Please try a different payment method or contact support.`,
          semanticStyle: "error",
          toastId: "confirm-draft-event-sign-up-error",
        });
        return [];
      },
      onPayloadError: (errors) => {
        errorCallback?.(
          new Error(errors[0]?.message || t`Unable to complete event signup.`)
        );
        showToast({
          description: t`We couldn't complete your signup. Please try a different payment method or contact support.`,
          semanticStyle: "error",
          toastId: "confirm-draft-event-sign-up-error",
        });
      },
      onErrorCustom: errorCallback,
    });
  };

  return [handleConfirmDraftEventSignUp, isConfirming] as const;
};

const useConfirmDraftEventSignUpMutationString = graphql`
  mutation useConfirmDraftEventSignUpMutation(
    $input: ConfirmDraftEventSignUpInput!
  ) {
    responsePayload: confirmDraftEventSignUp(input: $input) {
      eventSignUp {
        id
        status
        ...EventSignupSummaryContent_signUp @arguments(patronLoyalty: false)
      }
      errors {
        field
        code
        message
      }
      success
    }
  }
`;
